import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router';
import App from './App';
import { RequireSession } from './session';
import HardwarePage from './pages/Hardware';
import TermsPage from './pages/Terms';
import EmailWordingPage from './pages/EmailWording';
import AddOnsPage from './pages/AddOns';
import SeriesPage from './pages/Series';
import OpponentsPage from './pages/Opponents';
import SeasonsPage from './pages/Seasons';
import VouchersPage from './pages/Vouchers';
import SeasonPassesPage from './pages/SeasonPasses';
import { SectionTabs } from './components/SectionTabs';
import { GROUP_SALES_TABS, SETTINGS_TABS, STAFF_TABS } from './nav';
import AssistiveDevicesPage from './pages/AssistiveDevices';
import { BrandingPage } from './pages/Branding';
import EventPage from './pages/Event';
import EventsPage from './pages/Events';
import FeeRulesPage from './pages/FeeRules';
import LoginPage from './pages/Login';
import PendingPage from './pages/Pending';
import ManifestPage from './pages/Manifest';
import StaffPage from './pages/Staff';
import VenuePage from './pages/Venue';
import VenuesPage from './pages/Venues';
import SystemPage from './pages/System';
import SettingsPage from './pages/Settings';
import DonationsPage from './pages/Donations';
import OrdersPage from './pages/Orders';
import NewOrderPage from './pages/NewOrder';
import BalancesPage from './pages/Balances';
import HouseAccountsPage from './pages/HouseAccounts';
import GroupHoldsPage from './pages/GroupHolds';
import GroupHoldDetailPage from './pages/GroupHoldDetail';
import GroupSalesPage from './pages/GroupSales';
import GroupLeadPage from './pages/GroupLead';
import GroupBoardPage from './pages/GroupBoard';
import GroupSpacesPage from './pages/GroupSpaces';
import GroupReportsPage from './pages/GroupReports';
import DashboardPage from './pages/Dashboard';
import AuditPage from './pages/Audit';
import DisputesPage from './pages/Disputes';
import FulfillmentPage from './pages/Fulfillment';
import DoorPage from './pages/Door';
import ReportsPage from './pages/Reports';
import PackagesPage from './pages/Packages';
import CustomersPage from './pages/Customers';
import PayoutsPage from './pages/Payouts';
import QuickBooksPage from './pages/QuickBooks';
import MigrationPage from './pages/Migration';
import PromoCodesPage from './pages/PromoCodes';
import GamePromotionsPage from './pages/GamePromotions';
import BoxOfficePage from './pages/BoxOffice';
import ShiftsPage from './pages/Shifts';
import TicketTemplatesPage from './pages/TicketTemplates';
import MediaPage from './pages/Media';
import SponsorsPage from './pages/Sponsors';
import OrganizationsPage from './pages/Organizations';
import HoldsPage from './pages/Holds';
import TicketDesignsPage from './pages/TicketDesigns';
import PaymentsPage from './pages/Payments';
import PlatformPage from './pages/Platform';
import StripeConnectReturnPage from './pages/StripeConnectReturn';
import './styles.css';
import { loadTerms } from './terms';

// Authorization is decided server-side on every request, so the client cache
// is a convenience only. It is kept short and never revalidates behind the
// operator's back mid-task.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, staleTime: 15_000, refetchOnWindowFocus: false },
  },
});

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: (
      <RequireSession>
        <App />
      </RequireSession>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'events', element: <EventsPage /> },
      { path: 'events/:id', element: <EventPage /> },
      { path: 'fees', element: <FeeRulesPage /> },
      { path: 'staff', element: <SectionTabs tabs={STAFF_TABS}><StaffPage /></SectionTabs> },
      { path: 'staff/pending', element: <SectionTabs tabs={STAFF_TABS}><PendingPage /></SectionTabs> },
      { path: 'venues', element: <VenuesPage /> },
      { path: 'venues/:id', element: <VenuePage /> },
      { path: 'venues/:id/box-office', element: <BoxOfficePage /> },
      { path: 'box-office/shifts', element: <ShiftsPage /> },
      { path: 'box-office/shifts/:id', element: <ShiftsPage /> },
      { path: 'ticket-templates', element: <TicketTemplatesPage /> },
      { path: 'media', element: <MediaPage /> },
      { path: 'sponsors', element: <SponsorsPage /> },
      { path: 'organizations', element: <OrganizationsPage /> },
      { path: 'holds', element: <HoldsPage /> },
      { path: 'ticket-designs', element: <TicketDesignsPage /> },
      { path: 'ticket-designs/:id', element: <TicketDesignsPage /> },
      { path: 'manifests/:id', element: <ManifestPage /> },
      { path: 'hardware', element: <HardwarePage /> },
      { path: 'devices', element: <HardwarePage /> },
      { path: 'system', element: <SystemPage /> },
      { path: 'settings', element: <SectionTabs tabs={SETTINGS_TABS}><SettingsPage /></SectionTabs> },
      { path: 'settings/branding', element: <SectionTabs tabs={SETTINGS_TABS}><BrandingPage /></SectionTabs> },
      { path: 'settings/terms', element: <SectionTabs tabs={SETTINGS_TABS}><TermsPage /></SectionTabs> },
      { path: 'settings/email-wording', element: <SectionTabs tabs={SETTINGS_TABS}><EmailWordingPage /></SectionTabs> },
      { path: 'settings/payments', element: <SectionTabs tabs={SETTINGS_TABS}><PaymentsPage /></SectionTabs> },
      // STGD's section (D-393), and where Stripe returns after connecting an existing account.
      { path: 'platform', element: <PlatformPage /> },
      { path: 'stripe/connect-return', element: <StripeConnectReturnPage /> },
      { path: 'add-ons', element: <AddOnsPage /> },
      { path: 'series', element: <SeriesPage /> },
      { path: 'opponents', element: <OpponentsPage /> },
      { path: 'seasons', element: <SeasonsPage /> },
      { path: 'vouchers', element: <VouchersPage /> },
      { path: 'season-passes', element: <SeasonPassesPage /> },
      { path: 'assistive-devices', element: <AssistiveDevicesPage /> },
      { path: 'donations', element: <DonationsPage /> },
      { path: 'orders', element: <OrdersPage /> },
      { path: 'orders/new', element: <NewOrderPage /> },
      { path: 'balances', element: <BalancesPage /> },
      { path: 'house-accounts', element: <HouseAccountsPage /> },
      { path: 'group-holds', element: <GroupHoldsPage /> },
      { path: 'group-holds/:id', element: <GroupHoldDetailPage /> },
      { path: 'group-sales', element: <SectionTabs tabs={GROUP_SALES_TABS}><GroupSalesPage /></SectionTabs> },
      { path: 'group-sales/board', element: <SectionTabs tabs={GROUP_SALES_TABS}><GroupBoardPage /></SectionTabs> },
      { path: 'group-sales/spaces', element: <SectionTabs tabs={GROUP_SALES_TABS}><GroupSpacesPage /></SectionTabs> },
      { path: 'group-sales/reports', element: <SectionTabs tabs={GROUP_SALES_TABS}><GroupReportsPage /></SectionTabs> },
      { path: 'group-sales/leads/:id', element: <GroupLeadPage /> },
      { path: 'disputes', element: <DisputesPage /> },
      { path: 'audit', element: <AuditPage /> },
      { path: 'fulfillment', element: <FulfillmentPage /> },
      { path: 'door', element: <DoorPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'packages', element: <PackagesPage /> },
      { path: 'packages/:id', element: <PackagesPage /> },
      { path: 'customers', element: <CustomersPage /> },
      { path: 'customers/:key', element: <CustomersPage /> },
      { path: 'payouts', element: <PayoutsPage /> },
      { path: 'quickbooks', element: <QuickBooksPage /> },
      { path: 'migration', element: <MigrationPage /> },
      // Promo codes took this address from "Promotions" (D-377); an old
      // `/promotions/<promo code id>` link is forwarded by the page itself.
      { path: 'promo-codes', element: <PromoCodesPage /> },
      { path: 'promo-codes/:id', element: <PromoCodesPage /> },
      // The Promotions library: theme nights and specials (D-377).
      { path: 'promotions', element: <GamePromotionsPage /> },
      { path: 'promotions/:id', element: <GamePromotionsPage /> },
      { path: 'payouts/:id', element: <PayoutsPage /> },
      { path: 'reports/:tab', element: <ReportsPage /> },
      { path: 'orders/:id', element: <OrdersPage /> },
    ],
  },
]);

const container = document.getElementById('root');
if (!container) throw new Error('No #root element');

// The deployment's words before the first screen (D-378), so no label flips
// from "Dates" to "Games" after it is drawn. Never waits more than a moment.
void loadTerms().finally(() => {
  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
